import {
  EmailTemplateSender,
  IEmailTemplateSummary,
} from "@/models/admin/emailTemplates/emailTemplates.types"
import { IExternalEmailTemplateSummary } from "@/models/admin/emailTemplates/externalEmailTemplates.types"
import { Dayjs } from "dayjs"
import { senderModifiedBy } from "./senderPresentation"

export interface CommunicationRow {
  // Unique across both sources, which share neither an id space nor a key.
  key: string
  sender: EmailTemplateSender
  readOnly: boolean
  communicationId: number | null
  externalId: number | null
  communicationName: string
  communicationType: string | null
  liveSubject: string | null
  liveVersion: number | null
  latestVersion: number
  latestTemplateId: number | null
  // "v4" when live is latest; "v4 (v5 latest)" when a newer version is unpublished.
  versionLabel: string
  livePublishedAt: Dayjs | null
  liveModifiedBy: string | null
  versionCount: number
  searchText: string
}

function buildVersionLabel(
  liveVersion: number | null,
  latestVersion: number,
): string {
  if (liveVersion === null) return `No live (v${latestVersion} latest)`
  if (liveVersion === latestVersion) return `v${liveVersion}`
  return `v${liveVersion} (v${latestVersion} latest)`
}

/**
 * One row per communication. Templates are versions of the same communication,
 * so they are collapsed into a single row showing the live version's details.
 */
export function groupTemplatesByCommunication(
  templates: IEmailTemplateSummary[],
): CommunicationRow[] {
  const byCommunication = new Map<number, IEmailTemplateSummary[]>()
  for (const template of templates) {
    const group = byCommunication.get(template.communicationId) ?? []
    group.push(template)
    byCommunication.set(template.communicationId, group)
  }

  const rows: CommunicationRow[] = []
  for (const [communicationId, group] of byCommunication) {
    const live = group.find((template) => template.isLive) ?? null
    const latest = group.reduce(
      (latestSoFar, template) =>
        template.version > latestSoFar.version ? template : latestSoFar,
      group[0],
    )
    const latestVersion = latest.version
    const { communication } = group[0]
    const searchText = [
      communication.name,
      EmailTemplateSender.NOVA,
      ...group.map((template) => template.subject),
    ]
      .join(" ")
      .toLowerCase()

    rows.push({
      key: `nova-${communicationId}`,
      sender: EmailTemplateSender.NOVA,
      readOnly: false,
      communicationId,
      externalId: null,
      communicationName: communication.name,
      communicationType: communication.communicationType,
      liveSubject: live?.subject ?? null,
      liveVersion: live?.version ?? null,
      latestVersion,
      latestTemplateId: latest.id,
      versionLabel: buildVersionLabel(live?.version ?? null, latestVersion),
      livePublishedAt: live?.publishedAt ?? null,
      liveModifiedBy: live?.modifiedBy ?? null,
      versionCount: group.length,
      searchText,
    })
  }

  return sortRows(rows)
}

/**
 * External templates mirror a single record per name, so each one is its own
 * row. The synced version is always the one that sends, so it counts as live.
 */
export function buildExternalRows(
  templates: IExternalEmailTemplateSummary[],
): CommunicationRow[] {
  return sortRows(
    templates.map((template) => ({
      key: `${template.sender}-${template.id}`.toLowerCase(),
      sender: template.sender,
      readOnly: true,
      communicationId: null,
      externalId: template.id,
      communicationName: template.name,
      communicationType: null,
      liveSubject: template.subject,
      liveVersion: template.version,
      latestVersion: template.version,
      latestTemplateId: null,
      versionLabel: `v${template.version}`,
      livePublishedAt: template.updatedAt,
      liveModifiedBy: senderModifiedBy(template.sender),
      versionCount: 1,
      searchText: [template.name, template.sender, template.subject]
        .join(" ")
        .toLowerCase(),
    })),
  )
}

export function mergeRows(
  internal: CommunicationRow[],
  external: CommunicationRow[],
): CommunicationRow[] {
  return sortRows([...internal, ...external])
}

function sortRows(rows: CommunicationRow[]): CommunicationRow[] {
  return [...rows].sort((a, b) =>
    a.communicationName.localeCompare(b.communicationName),
  )
}

export function filterRows(
  rows: CommunicationRow[],
  query: string | null | undefined,
  sender?: EmailTemplateSender | null,
): CommunicationRow[] {
  const trimmed = (query ?? "").trim().toLowerCase()
  return rows.filter((row) => {
    if (sender && row.sender !== sender) return false
    return !trimmed || row.searchText.includes(trimmed)
  })
}

/** Versions are immutable; a new version is max(existing) + 1, or 1 if none. */
export function nextVersion(
  templates: IEmailTemplateSummary[],
  communicationId: number,
): number {
  const versions = templates
    .filter((template) => template.communicationId === communicationId)
    .map((template) => template.version)
  return versions.length ? Math.max(...versions) + 1 : 1
}
